import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { WorkOrderType, canActOnWorkOrderType } from '@signalvote/shared';
import { getApiClient } from '@signalvote/api-client';
import { ExpressSend, SEND_STEP_ORDER, type SendStepKey } from '@/components/express';
import { useCampaignStore } from '@/stores';

// The selected campaign's Text send: the Express send steps. Only someone who may order a
// Text send for the selected campaign gets in; anyone else goes to the app. `step` is where to start,
// such as where sign-up left off. `campaign` makes a resume link: the page switches to that campaign
// first (the API refuses one the person can't open), so a link sent to a customer lands on their text.
const searchSchema = z.object({
  step: z
    .custom<SendStepKey>((value) => (SEND_STEP_ORDER as readonly unknown[]).includes(value))
    .optional()
    .catch(undefined),
  campaign: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute('/express/send')({
  validateSearch: searchSchema,
  beforeLoad: async ({ context, location, search }) => {
    if (!context.auth.user) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    const store = useCampaignStore.getState();
    if (search.campaign && store.selectedCampaign?.id !== search.campaign) {
      // A stale selected campaign would ride along as X-Campaign-Id and fail the switch.
      store.selectCampaign(null);
      try {
        const { campaign } = await getApiClient().tenantCampaigns.switchCampaign({ campaignId: search.campaign });
        store.selectCampaign(campaign);
      } catch {
        throw redirect({ to: '/app' });
      }
    }
    const { selectedCampaign, isFullAdmin } = useCampaignStore.getState();
    const allowed =
      selectedCampaign &&
      canActOnWorkOrderType(WorkOrderType.TextSend, {
        isPlatformAdmin: context.auth.user.isPlatformAdmin,
        isFullAdmin: isFullAdmin(),
        workOrdersEnabled: selectedCampaign.workOrdersEnabled,
      });
    if (!allowed) throw redirect({ to: '/app' });
  },
  component: ExpressSendPage,
});

function ExpressSendPage() {
  const { step } = Route.useSearch();
  const campaign = useCampaignStore((s) => s.selectedCampaign);
  if (!campaign) return null;
  return <ExpressSend key={campaign.id} campaign={campaign} startAt={step} />;
}
